import { useQueries } from '@tanstack/react-query';
import React, { memo, useEffect, useMemo } from 'react';
import { getGeneralCareNotificationQueryOptions } from '../../api/get-general-care-notification.api';
import { DEFAULT_GENERAL_CARE_NOTIFICATION } from '@/app/settings/setting-notification-page/care-notification-tab/care-notification-tab.constant';
import { getGeneralTicketingNotificationQueryOptions } from '../../api/get-general-ticketing-notification.api';
import { DEFAULT_GENERAL_TICKETING_NOTIFICATION } from '@/app/settings/setting-notification-page/ticketing-notification-tab/ticketing-notification-tab.constant';
import { useNotificationStoreActions } from '../../stores/new-notification.store';
import useAuthContext from '@/features/auth/_shared/contexts/auth.context';

interface INotificationProviderProps {
  children: React.ReactNode;
}

/**
 * NotificationProvider - Production-grade provider for notification state management
 *
 * Features:
 * - Prevents memory leaks with isMounted pattern
 * - Optimized re-renders with useCallback and useMemo
 * - Proper cleanup of all subscriptions and intervals
 * - Handles route-based sound page detection
 * - Tracks browser visibility state
 * - Monitors browser notification permissions
 */
const NotificationProvider = memo(({ children }: INotificationProviderProps) => {
  const { isTicketingRole, hydrated, state, isAuthenticated, isExternalUserRole } = useAuthContext();
  const { setIsOnActiveCaseSoundPage, setIsOnUnassignedCaseSoundPage, setIsOnTicketingSoundPage } =
    useNotificationStoreActions();

  // Ref to track mount status and prevent state updates after unmount
  // const isMountedRef = useRef(true);

  const [
    {
      data: generalCareNotification,
      // isLoading: _isLoadingGeneralCareNotification,
      // isFetching: _isFetchingGeneralCareNotification,
    },
    {
      data: generalTicketingNotification,
      // isLoading: _isLoadingGeneralTicketingNotification,
      // isFetching: _isFetchingGeneralTicketingNotification,
    },
  ] = useQueries({
    queries: [
      {
        ...getGeneralCareNotificationQueryOptions((data) => {
          if (data?.payload) {
            return {
              overall: data?.payload?.config?.overall,
              activeCaseSound: data?.payload?.config?.activeCaseSound,
              unassignedCaseSound: data?.payload?.config?.unassignedCaseSound,
              activeCount: data?.payload?.activeCount,
            };
          }
          return DEFAULT_GENERAL_CARE_NOTIFICATION;
        }),
        enabled: hydrated && !state?.loading && isAuthenticated && !isTicketingRole && !isExternalUserRole,
        staleTime: 0,
        gcTime: 0,
      },
      {
        ...getGeneralTicketingNotificationQueryOptions((data) => {
          if (data?.payload) {
            return {
              overall: data?.payload?.config?.overall,
              sound: data?.payload?.config?.sound,
              activeCount: data?.payload?.activeCount,
            };
          }
          return DEFAULT_GENERAL_TICKETING_NOTIFICATION;
        }),
        enabled: hydrated && !state?.loading && isAuthenticated && !isExternalUserRole,
        staleTime: 0,
        gcTime: 0,
      },
    ],
  });

  // Memoize notification config flags to prevent unnecessary recalculations
  const notificationConfig = useMemo(() => {
    return {
      careOverallEnabled: generalCareNotification?.overall ?? false,
      careActiveCaseSoundEnabled: generalCareNotification?.activeCaseSound ?? false,
      careUnassignedCaseSoundEnabled: generalCareNotification?.unassignedCaseSound ?? false,
      ticketingOverallEnabled: generalTicketingNotification?.overall ?? false,
      ticketingSoundEnabled: generalTicketingNotification?.sound ?? false,
    };
  }, [
    generalCareNotification?.overall,
    generalCareNotification?.activeCaseSound,
    generalCareNotification?.unassignedCaseSound,
    generalTicketingNotification?.overall,
    generalTicketingNotification?.sound,
  ]);

  // Update route-based sound page states
  useEffect(() => {
    // if (!isMountedRef.current) return;

    // const { isOnActiveCasePage, isOnUnassignedCasePage, isOnTicketingPage } = routeStates;
    const {
      careOverallEnabled,
      careActiveCaseSoundEnabled,
      careUnassignedCaseSoundEnabled,
      ticketingOverallEnabled,
      ticketingSoundEnabled,
    } = notificationConfig;

    // Only set to true if on the page AND sound is enabled in config
    setIsOnActiveCaseSoundPage(careActiveCaseSoundEnabled && careOverallEnabled);
    setIsOnUnassignedCaseSoundPage(careUnassignedCaseSoundEnabled && careOverallEnabled);
    setIsOnTicketingSoundPage(ticketingSoundEnabled && ticketingOverallEnabled);
  }, [notificationConfig, setIsOnActiveCaseSoundPage, setIsOnUnassignedCaseSoundPage, setIsOnTicketingSoundPage]);

  return <>{children}</>;
});
NotificationProvider.displayName = 'NotificationProvider';

export default NotificationProvider;
